<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { TableFilterCondition } from "../../../types/TableFilter.types";
import VDatePicker from "../../DateTime/VDatePicker.vue";
import FilterConditionSelect from "./FilterConditionSelect.vue";

interface Props {
  modelValue: [Date, Date] | Date | null
  condition: TableFilterCondition
  placeholder?: string
  /** Id of the element that names the filter; its controls are labelled by it. */
  labelId?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  labelId: undefined,
});

const emit = defineEmits<{
  "update:modelValue": [value: [Date, Date] | Date | null]
  "update:condition": [condition: TableFilterCondition]
}>();

const { t } = useI18n();

const conditionOptions = [
  { value: TableFilterCondition.EQUALS, name: t("filters.conditions.equals") },
  { value: TableFilterCondition.BETWEEN, name: t("filters.conditions.between") },
  { value: TableFilterCondition.GREATER_THAN, name: t("filters.conditions.after") },
  { value: TableFilterCondition.LESS_THAN, name: t("filters.conditions.before") },
];

const localValue = ref(props.modelValue);
const localCondition = ref(props.condition);

watch(() => props.modelValue, (val) => {
  localValue.value = val;
});
watch(() => props.condition, (val) => {
  localCondition.value = val;
});

function updateValue(value: string | Date | Date[] | null) {
  if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
    localValue.value = [value[0], value[1]];
    emit("update:modelValue", [value[0], value[1]]);
    return;
  }
  if (value instanceof Date) {
    localValue.value = value;
    emit("update:modelValue", value);
    return;
  }
  localValue.value = null;
  emit("update:modelValue", null);
}

function updateCondition(condition: TableFilterCondition) {
  localCondition.value = condition;
  emit("update:condition", condition);
}

function isRange() {
  return localCondition.value === TableFilterCondition.BETWEEN;
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <FilterConditionSelect
      :model-value="localCondition"
      :options="conditionOptions"
      :label-id="labelId"
      @update:model-value="updateCondition"
    />
    <VDatePicker
      :model-value="localValue"
      :aria-labelledby="labelId"
      :range="isRange()"
      :presets="isRange() ? 'back' : undefined"
      :placeholder="placeholder"
      @update:model-value="updateValue"
    />
  </div>
</template>
